'use client'

import { useEffect, useState } from 'react'
import { createGlobalState, useMount } from 'react-use'
import { ampli, type BaseEvent } from '../codegen/ampli'

// Create a global state to track if Ampli is initialized on all components
const useAmpliIsInitialized = createGlobalState(false)

/** Do-it-all hook for initing Amplitude and logging custom events on page load or at any time.
 *
 *  Events can be logged on mount like so:
 *  @example
 * ```tsx
 *  useAmplitudeAnalytics({
 *    onLoadEvent: new WalletHistoryViewed(),
 *  })
 * ```
 */
export function useAmplitudeAnalytics({
  init,
  onMountEvent: onLoadEvent,
}: {
  /** Init analytics environment. Done once per user session. */
  init?: boolean
  /** Log this event when the component mounts once.
   *
   *  @example
   * ```tsx
   *  useAmplitudeAnalytics({
   *    onLoadEvent: new WalletHistoryViewed(),
   *  })
   * ```
   */
  onMountEvent?: BaseEvent
} = {}) {
  const [hasLoggedLoadEvent, setHasLoggedLoadEvent] = useState(false)
  const [isInitialized, setIsInitialized] = useAmpliIsInitialized()

  useMount(() => {
    const initializeAmpli = async () => {
      if (process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY === undefined) {
        return
      }

      if (ampli.isLoaded) {
        setIsInitialized(true)
        return
      }

      ampli
        .load({
          client: {
            apiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
            configuration: {
              serverUrl: process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_URL,
              autocapture: {
                pageViews: false,
                formInteractions: false,
              },
            },
          },
        })
        .promise.then(() => {
          setIsInitialized(true)
        })
    }

    if (init) {
      initializeAmpli()
    }
  })

  useEffect(() => {
    if (isInitialized && !hasLoggedLoadEvent && onLoadEvent) {
      ampli.track(onLoadEvent)
      setHasLoggedLoadEvent(true)
    }
  }, [isInitialized, hasLoggedLoadEvent, onLoadEvent])

  return {
    ampli: isInitialized ? ampli : undefined,
    isInitialized,
  }
}
